import { cn } from '../utils/cn';

interface StatusBadgeProps {
  status: string;
  className?: string;
}

/**
 * Map every label the backend or frontend might pass into one of three
 * tones: success (green), warning (amber), danger (red), or neutral (grey
 * — the default). The keys are lower-cased; lookups are case-insensitive.
 *
 * Keep in sync with `enumLabel(...)` outputs from `@kss/api`'s enums.ts.
 */
const statusStyles: Record<string, string> = {
  // Generic
  active: 'bg-success-light text-success',
  inactive: 'bg-surface-card-hover text-content-muted',
  pending: 'bg-warning-light text-warning',
  suspended: 'bg-danger-light text-danger',
  completed: 'bg-success-light text-success',
  cancelled: 'bg-danger-light text-danger',

  // SalesOrder / PurchaseOrder
  draft: 'bg-warning-light text-warning',
  confirmed: 'bg-info-light text-info',
  shipped: 'bg-info-light text-info',
  delivered: 'bg-success-light text-success',
  sent: 'bg-info-light text-info',
  received: 'bg-success-light text-success',
  partiallyreceived: 'bg-warning-light text-warning',
  /* SSO NEW ISSUE 3 — SalesOrderStatus=6 label. Uses the same danger tone
     as cancelled so the panel All Orders grid and the storefront's
     Returns & Refunds section render a consistent, easy-to-spot pill. */
  returned: 'bg-danger-light text-danger',

  // Invoice
  issued: 'bg-info-light text-info',
  paid: 'bg-success-light text-success',
  partiallypaid: 'bg-warning-light text-warning',
  overdue: 'bg-danger-light text-danger',

  // Payment
  unpaid: 'bg-warning-light text-warning',
  partial: 'bg-warning-light text-warning',
  refunded: 'bg-info-light text-info',

  // Attendance
  present: 'bg-success-light text-success',
  absent: 'bg-danger-light text-danger',
  leave: 'bg-warning-light text-warning',
  halfday: 'bg-warning-light text-warning',
  holiday: 'bg-info-light text-info',
  weeklyoff: 'bg-surface-card-hover text-content-muted',

  // User / License / Tenant
  trial: 'bg-info-light text-info',
  expired: 'bg-danger-light text-danger',
  disabled: 'bg-danger-light text-danger',
  locked: 'bg-danger-light text-danger',
  pendingverification: 'bg-warning-light text-warning',
  deactivated: 'bg-danger-light text-danger',

  // Employee
  onleave: 'bg-warning-light text-warning',
  terminated: 'bg-danger-light text-danger',
  resigned: 'bg-danger-light text-danger',

  // Payroll
  approved: 'bg-info-light text-info',

  // Journal
  posted: 'bg-success-light text-success',
  reversed: 'bg-warning-light text-warning',

  // Stock movement
  in: 'bg-success-light text-success',
  out: 'bg-info-light text-info',
  transfer: 'bg-info-light text-info',
  adjustment: 'bg-warning-light text-warning',
  return: 'bg-warning-light text-warning',

  // Order return string status (free-text on backend)
  requested: 'bg-warning-light text-warning',
  rejected: 'bg-danger-light text-danger',
};

export default function StatusBadge({ status, className }: StatusBadgeProps) {
  const key = String(status ?? '').toLowerCase().replace(/\s+/g, '');
  const style = statusStyles[key] || 'bg-surface-card-hover text-content-muted';

  return (
    <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize', style, className)}>
      {status}
    </span>
  );
}
